/*!

 * DH Commerce OS v1.4

 * dh-commerce-core.js

 *

 * Shared foundation for every DH Commerce engine.

 * Provides namespace creation, utilities, event bus, state, storage,

 * logging, diagnostics, engine/plugin registration, lifecycle hooks,

 * health reporting, analytics forwarding, and defensive error handling.

 */

(function (window, document) {

  "use strict";

 

  var ROOT = window.DHCommerce = window.DHCommerce || {};

  var VERSION = "1.4.0";

  var BUILD = "core-2026.07.17";

  var STARTED_AT = Date.now();

  var hasOwn = Object.prototype.hasOwnProperty;

  var toString = Object.prototype.toString;

 

  function noop() {}

  function identity(value) { return value; }

  function isFunction(value) { return typeof value === "function"; }

  function isString(value) { return typeof value === "string"; }

  function isNumber(value) { return typeof value === "number" && Number.isFinite(value); }

  function isObject(value) { return value !== null && typeof value === "object"; }

  function isPlainObject(value) { return toString.call(value) === "[object Object]"; }

  function isPromise(value) { return !!value && isFunction(value.then); }

  function now() { return Date.now(); }

  function isoNow() { return new Date().toISOString(); }

 

  function clone(value, seen) {

    if (!isObject(value)) return value;

    if (value instanceof Date) return new Date(value.getTime());

    if (value instanceof RegExp) return new RegExp(value.source, value.flags);

    seen = seen || [];

    for (var i = 0; i < seen.length; i += 1) {

      if (seen[i][0] === value) return seen[i][1];

    }

    var output = Array.isArray(value) ? [] : {};

    seen.push([value, output]);

    Object.keys(value).forEach(function (key) {

      output[key] = clone(value[key], seen);

    });

    return output;

  }

 

  function merge(target) {

    var output = isPlainObject(target) ? clone(target) : {};

    for (var i = 1; i < arguments.length; i += 1) {

      var source = arguments[i];

      if (!isPlainObject(source)) continue;

      Object.keys(source).forEach(function (key) {

        var incoming = source[key];

        if (isPlainObject(incoming) && isPlainObject(output[key])) {

          output[key] = merge(output[key], incoming);

        } else {

          output[key] = clone(incoming);

        }

      });

    }

    return output;

  }

 

  function freezeDeep(value) {

    if (!isObject(value) || Object.isFrozen(value)) return value;

    Object.keys(value).forEach(function (key) { freezeDeep(value[key]); });

    try { Object.freeze(value); } catch (error) { noop(error); }

    return value;

  }

 

  function getPath(object, path, fallback) {

    if (!path) return object === undefined ? fallback : object;

    var parts = Array.isArray(path) ? path : String(path).split(".");

    var cursor = object;

    for (var i = 0; i < parts.length; i += 1) {

      if (cursor === null || cursor === undefined || !hasOwn.call(Object(cursor), parts[i])) {

        return fallback;

      }

      cursor = cursor[parts[i]];

    }

    return cursor === undefined ? fallback : cursor;

  }

 

  function setPath(object, path, value) {

    if (!isObject(object)) throw new TypeError("setPath requires an object");

    var parts = Array.isArray(path) ? path.slice() : String(path).split(".");

    if (!parts.length) return object;

    var cursor = object;

    for (var i = 0; i < parts.length - 1; i += 1) {

      var key = parts[i];

      if (!isPlainObject(cursor[key])) cursor[key] = {};

      cursor = cursor[key];

    }

    cursor[parts[parts.length - 1]] = value;

    return object;

  }

 

  function deletePath(object, path) {

    if (!isObject(object)) return false;

    var parts = Array.isArray(path) ? path.slice() : String(path).split(".");

    var cursor = object;

    for (var i = 0; i < parts.length - 1; i += 1) {

      if (!isObject(cursor[parts[i]])) return false;

      cursor = cursor[parts[i]];

    }

    return delete cursor[parts[parts.length - 1]];

  }

 

  function uniqueId(prefix) {

    prefix = prefix || "dh";

    var random = Math.random().toString(36).slice(2, 10);

    return prefix + "_" + now().toString(36) + "_" + random;

  }

 

  function debounce(fn, wait) {

    var timer = null;

    return function () {

      var context = this;

      var args = arguments;

      window.clearTimeout(timer);

      timer = window.setTimeout(function () { fn.apply(context, args); }, Number(wait) || 0);

    };

  }

 

  function throttle(fn, wait) {

    var last = 0;

    var timer = null;

    return function () {

      var context = this;

      var args = arguments;

      var remaining = (Number(wait) || 0) - (now() - last);

      if (remaining <= 0) {

        window.clearTimeout(timer);

        timer = null;

        last = now();

        fn.apply(context, args);

      } else if (!timer) {

        timer = window.setTimeout(function () {

          last = now();

          timer = null;

          fn.apply(context, args);

        }, remaining);

      }

    };

  }

 

  function safeJsonParse(value, fallback) {

    try { return JSON.parse(value); } catch (error) { return fallback; }

  }

 

  function safeJsonStringify(value, fallback) {

    try { return JSON.stringify(value); } catch (error) { return fallback === undefined ? null : fallback; }

  }

 

  function hashString(value) {

    var text = String(value || "");

    var hash = 2166136261;

    for (var i = 0; i < text.length; i += 1) {

      hash ^= text.charCodeAt(i);

      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);

    }

    return (hash >>> 0).toString(36);

  }

 

  function compareVersions(a, b) {

    var left = String(a || "0").split(".");

    var right = String(b || "0").split(".");

    var length = Math.max(left.length, right.length);

    for (var i = 0; i < length; i += 1) {

      var x = parseInt(left[i] || "0", 10);

      var y = parseInt(right[i] || "0", 10);

      if (x > y) return 1;

      if (x < y) return -1;

    }

    return 0;

  }

 

  function satisfiesVersion(actual, required) {

    if (!required) return true;

    actual = String(actual || "0.0.0");

    required = String(required).trim();

    if (required.indexOf(">=") === 0) return compareVersions(actual, required.slice(2)) >= 0;

    if (required.indexOf(">") === 0) return compareVersions(actual, required.slice(1)) > 0;

    if (required.indexOf("<=") === 0) return compareVersions(actual, required.slice(2)) <= 0;

    if (required.indexOf("<") === 0) return compareVersions(actual, required.slice(1)) < 0;

    if (required.indexOf("^") === 0) {

      var major = required.slice(1).split(".")[0];

      return actual.split(".")[0] === major && compareVersions(actual, required.slice(1)) >= 0;

    }

    return compareVersions(actual, required) === 0;

  }

 

  var diagnostics = {

    version: VERSION,

    build: BUILD,

    createdAt: isoNow(),

    events: [],

    logs: [],

    errors: [],

    warnings: [],

    engines: {},

    plugins: {},

    hooks: {},

    counters: {},

    limits: { events: 250, logs: 250, errors: 100, warnings: 100 }

  };

 

  function trimList(list, maximum) {

    if (list.length > maximum) list.splice(0, list.length - maximum);

  }

 

  function increment(name, amount) {

    diagnostics.counters[name] = Number(diagnostics.counters[name] || 0) + (Number(amount) || 1);

    return diagnostics.counters[name];

  }

 

  function configGet(path, fallback) {

    if (ROOT.config && isFunction(ROOT.config.get)) {

      try { return ROOT.config.get(path, fallback); } catch (error) { return fallback; }

    }

    return getPath(ROOT.config, path, fallback);

  }

 

  function debugEnabled() { return !!configGet("runtime.debug", false); }

 

  function normalizeLogData(data) {

    if (data instanceof Error) {

      return { name: data.name, message: data.message, stack: data.stack || null };

    }

    return data === undefined ? null : data;

  }

 

  function writeLog(level, message, data, context) {

    var entry = {

      id: uniqueId("log"),

      level: level,

      message: String(message || ""),

      data: normalizeLogData(data),

      context: context || null,

      timestamp: isoNow()

    };

    diagnostics.logs.push(entry);

    trimList(diagnostics.logs, diagnostics.limits.logs);

    increment("logs.total");

    increment("logs." + level);

    if (level === "debug" && !debugEnabled()) return entry;

    var consoleMethod = window.console && (window.console[level] || window.console.log);

    if (isFunction(consoleMethod)) {

      try { consoleMethod.call(window.console, "[DHCommerce] " + entry.message, data === undefined ? "" : data); } catch (error) { noop(error); }

    }

    return entry;

  }

 

  var logger = {

    debug: function (message, data, context) { return writeLog("debug", message, data, context); },

    info: function (message, data, context) { return writeLog("info", message, data, context); },

    warn: function (message, data, context) { return writeLog("warn", message, data, context); },

    error: function (message, data, context) { return writeLog("error", message, data, context); },

    child: function (context) {

      return {

        debug: function (message, data) { return writeLog("debug", message, data, context); },

        info: function (message, data) { return writeLog("info", message, data, context); },

        warn: function (message, data) { return writeLog("warn", message, data, context); },

        error: function (message, data) { return writeLog("error", message, data, context); }

      };

    },

    getAll: function () { return clone(diagnostics.logs); },

    clear: function () { diagnostics.logs.length = 0; }

  };

 

  var eventListeners = {};

  var anyListeners = [];

 

  function listenerRecord(handler, options) {

    return {

      id: uniqueId("listener"),

      handler: handler,

      once: !!(options && options.once),

      priority: Number(options && options.priority) || 0

    };

  }

 

  function on(name, handler, options) {

    if (!isString(name) || !isFunction(handler)) throw new TypeError("events.on requires an event name and handler");

    eventListeners[name] = eventListeners[name] || [];

    var record = listenerRecord(handler, options);

    eventListeners[name].push(record);

    eventListeners[name].sort(function (a, b) { return b.priority - a.priority; });

    return function () { off(name, handler); };

  }

 

  function once(name, handler, options) {

    options = merge(options || {}, { once: true });

    return on(name, handler, options);

  }

 

  function onAny(handler, options) {

    if (!isFunction(handler)) throw new TypeError("events.onAny requires a handler");

    var record = listenerRecord(handler, options);

    anyListeners.push(record);

    anyListeners.sort(function (a, b) { return b.priority - a.priority; });

    return function () {

      anyListeners = anyListeners.filter(function (item) { return item.handler !== handler; });

    };

  }

 

  function off(name, handler) {

    if (!eventListeners[name]) return false;

    var before = eventListeners[name].length;

    eventListeners[name] = eventListeners[name].filter(function (record) {

      return handler ? record.handler !== handler : false;

    });

    if (!handler) eventListeners[name] = [];

    return before !== eventListeners[name].length;

  }

 

  function dispatchDomEvent(name, payload) {

    try {

      var event;

      if (typeof window.CustomEvent === "function") {

        event = new CustomEvent(name, { detail: payload });

      } else {

        event = document.createEvent("CustomEvent");

        event.initCustomEvent(name, false, false, payload);

      }

      document.dispatchEvent(event);

    } catch (error) {

      logger.warn("DOM event dispatch failed: " + name, error);

    }

  }

 

  function emit(name, detail, options) {

    options = options || {};

    var envelope = {

      id: uniqueId("event"),

      name: name,

      detail: detail === undefined ? {} : detail,

      timestamp: isoNow(),

      timestampMs: now(),

      source: options.source || "core",

      version: VERSION

    };

    diagnostics.events.push(clone(envelope));

    trimList(diagnostics.events, diagnostics.limits.events);

    increment("events.total");

    increment("events." + name);

 

    var records = (eventListeners[name] || []).slice();

    records.forEach(function (record) {

      try { record.handler(envelope.detail, envelope); }

      catch (error) { captureError(error, { code: "EVENT_HANDLER_FAILED", event: name, listenerId: record.id }); }

      if (record.once) off(name, record.handler);

    });

 

    anyListeners.slice().forEach(function (record) {

      try { record.handler(name, envelope.detail, envelope); }

      catch (error) { captureError(error, { code: "ANY_EVENT_HANDLER_FAILED", event: name, listenerId: record.id }); }

      if (record.once) anyListeners = anyListeners.filter(function (item) { return item !== record; });

    });

 

    if (options.dom !== false) dispatchDomEvent(name, envelope.detail);

    forwardAnalytics(envelope);

    return envelope;

  }

 

  function waitFor(name, timeoutMs, predicate) {

    return new Promise(function (resolve, reject) {

      var timer = null;

      var unsubscribe = on(name, function (detail, envelope) {

        if (predicate && !predicate(detail, envelope)) return;

        if (timer) window.clearTimeout(timer);

        unsubscribe();

        resolve({ detail: detail, envelope: envelope });

      });

      if (timeoutMs) {

        timer = window.setTimeout(function () {

          unsubscribe();

          reject(new Error("Timed out waiting for event: " + name));

        }, timeoutMs);

      }

    });

  }

 

  var events = {

    on: on,

    once: once,

    onAny: onAny,

    off: off,

    emit: emit,

    waitFor: waitFor,

    listenerCount: function (name) { return (eventListeners[name] || []).length; },

    clear: function (name) {

      if (name) eventListeners[name] = [];

      else { eventListeners = {}; anyListeners = []; }

    },

    getRecent: function (limit) { return clone(diagnostics.events.slice(-(Number(limit) || 50))); }

  };

 

  function captureError(error, context) {

    error = error instanceof Error ? error : new Error(String(error || "Unknown error"));

    var entry = {

      id: uniqueId("error"),

      code: context && context.code ? context.code : "UNHANDLED_ERROR",

      message: error.message,

      name: error.name,

      stack: error.stack || null,

      context: clone(context || {}),

      timestamp: isoNow()

    };

    diagnostics.errors.push(entry);

    trimList(diagnostics.errors, diagnostics.limits.errors);

    increment("errors.total");

    logger.error(entry.message, entry, entry.context);

    if (!(context && context.silent)) emit("dhcommerce:error", clone(entry), { source: "core", dom: true });

    return entry;

  }

 

  function warn(code, message, context) {

    var entry = { id: uniqueId("warning"), code: code, message: message, context: clone(context || {}), timestamp: isoNow() };

    diagnostics.warnings.push(entry);

    trimList(diagnostics.warnings, diagnostics.limits.warnings);

    increment("warnings.total");

    logger.warn(message, entry, context);

    emit("dhcommerce:warning", clone(entry), { source: "core" });

    return entry;

  }

 

  function safeCall(fn, context, args, fallback, errorContext) {

    if (!isFunction(fn)) return fallback;

    try { return fn.apply(context || null, args || []); }

    catch (error) { captureError(error, errorContext || { code: "SAFE_CALL_FAILED" }); return fallback; }

  }

 

  function toPromise(value) { return isPromise(value) ? value : Promise.resolve(value); }

 

  function withTimeout(value, timeoutMs, label) {

    var promise = toPromise(value);

    timeoutMs = Number(timeoutMs) || 0;

    if (timeoutMs <= 0) return promise;

    return new Promise(function (resolve, reject) {

      var timer = window.setTimeout(function () { reject(new Error((label || "Operation") + " timed out after " + timeoutMs + "ms")); }, timeoutMs);

      promise.then(function (result) { window.clearTimeout(timer); resolve(result); }, function (error) { window.clearTimeout(timer); reject(error); });

    });

  }

 

  var stateData = isPlainObject(ROOT.state) ? ROOT.state : {};

  var stateSubscribers = [];

  var stateTransactions = 0;

 

  function notifyState(change) {

    if (stateTransactions > 0) return;

    stateSubscribers.slice().forEach(function (subscriber) {

      if (subscriber.path && change.path.indexOf(subscriber.path) !== 0) return;

      safeCall(subscriber.handler, null, [clone(change), state.snapshot()], null, { code: "STATE_SUBSCRIBER_FAILED", path: subscriber.path });

    });

    emit("dhcommerce:state-change", clone(change), { source: "state" });

  }

 

  var state = {

    get: function (path, fallback) { return clone(getPath(stateData, path, fallback)); },

    peek: function (path, fallback) { return getPath(stateData, path, fallback); },

    set: function (path, value, options) {

      var previous = clone(getPath(stateData, path));

      setPath(stateData, path, clone(value));

      if (!(options && options.silent)) notifyState({ type: "set", path: path, previous: previous, value: clone(value), timestamp: isoNow() });

      return clone(value);

    },

    merge: function (path, value, options) {

      var current = getPath(stateData, path, {});

      var next = merge(isPlainObject(current) ? current : {}, value || {});

      return state.set(path, next, options);

    },

    remove: function (path, options) {

      var previous = clone(getPath(stateData, path));

      var removed = deletePath(stateData, path);

      if (removed && !(options && options.silent)) notifyState({ type: "remove", path: path, previous: previous, timestamp: isoNow() });

      return removed;

    },

    update: function (path, updater, options) {

      if (!isFunction(updater)) throw new TypeError("state.update requires an updater");

      return state.set(path, updater(state.get(path)), options);

    },

    transaction: function (handler) {

      stateTransactions += 1;

      try { return handler(state); }

      finally {

        stateTransactions -= 1;

        if (stateTransactions === 0) emit("dhcommerce:state-transaction", { state: state.snapshot() }, { source: "state" });

      }

    },

    subscribe: function (path, handler) {

      if (isFunction(path)) { handler = path; path = ""; }

      if (!isFunction(handler)) throw new TypeError("state.subscribe requires a handler");

      var record = { id: uniqueId("state"), path: String(path || ""), handler: handler };

      stateSubscribers.push(record);

      return function () { stateSubscribers = stateSubscribers.filter(function (item) { return item !== record; }); };

    },

    snapshot: function () { return clone(stateData); },

    replace: function (value, options) {

      stateData = isPlainObject(value) ? clone(value) : {};

      ROOT.state = stateData;

      if (!(options && options.silent)) notifyState({ type: "replace", path: "", value: state.snapshot(), timestamp: isoNow() });

      return state.snapshot();

    },

    reset: function () { return state.replace({}); }

  };

  ROOT.state = stateData;

 

  function storageAvailable(storage) {

    if (!storage) return false;

    try {

      var key = "__dh_test__";

      storage.setItem(key, "1");

      storage.removeItem(key);

      return true;

    } catch (error) { return false; }

  }

 

  function createStorageAdapter(storage, type) {

    var available = storageAvailable(storage);

    function namespace(key) { return String(configGet("storage.namespace", "dhcommerce")) + ":" + String(key); }

    function permitted() {

      var setting = type === "local" ? "privacy.allowLocalStorage" : "privacy.allowSessionStorage";

      return available && configGet(setting, true) !== false;

    }

    return {

      type: type,

      available: function () { return permitted(); },

      get: function (key, fallback) {

        if (!permitted()) return fallback;

        try {

          var raw = storage.getItem(namespace(key));

          if (!raw) return fallback;

          var record = safeJsonParse(raw, null);

          if (!record) return fallback;

          if (record.expiresAt && record.expiresAt <= now()) { storage.removeItem(namespace(key)); return fallback; }

          return clone(record.value);

        } catch (error) { captureError(error, { code: "STORAGE_READ_FAILED", type: type, key: key, silent: true }); return fallback; }

      },

      set: function (key, value, options) {

        if (!permitted()) return false;

        try {

          var ttl = Number(options && options.ttlMs) || 0;

          storage.setItem(namespace(key), safeJsonStringify({ value: value, createdAt: now(), expiresAt: ttl ? now() + ttl : null }));

          return true;

        } catch (error) { captureError(error, { code: "STORAGE_WRITE_FAILED", type: type, key: key, silent: true }); return false; }

      },

      remove: function (key) { if (!permitted()) return false; try { storage.removeItem(namespace(key)); return true; } catch (error) { return false; } },

      clear: function () {

        if (!permitted()) return 0;

        var prefix = namespace("");

        var keys = [];

        for (var i = 0; i < storage.length; i += 1) { var key = storage.key(i); if (key && key.indexOf(prefix) === 0) keys.push(key); }

        keys.forEach(function (key) { storage.removeItem(key); });

        return keys.length;

      },

      keys: function () {

        if (!permitted()) return [];

        var prefix = namespace("");

        var result = [];

        for (var i = 0; i < storage.length; i += 1) { var key = storage.key(i); if (key && key.indexOf(prefix) === 0) result.push(key.slice(prefix.length)); }

        return result;

      }

    };

  }

 

  var memoryStore = {};

  var storage = {

    local: createStorageAdapter(window.localStorage, "local"),

    session: createStorageAdapter(window.sessionStorage, "session"),

    memory: {

      get: function (key, fallback) { return hasOwn.call(memoryStore, key) ? clone(memoryStore[key]) : fallback; },

      set: function (key, value) { memoryStore[key] = clone(value); return true; },

      remove: function (key) { return delete memoryStore[key]; },

      clear: function () { memoryStore = {}; },

      keys: function () { return Object.keys(memoryStore); },

      available: function () { return true; }

    },

    best: function (preference) {

      if (preference === "session" && storage.session.available()) return storage.session;

      if (storage.local.available()) return storage.local;

      if (storage.session.available()) return storage.session;

      return storage.memory;

    }

  };

 

  var hookRegistry = {};

  function addHook(name, handler, options) {

    if (!isFunction(handler)) throw new TypeError("hooks.add requires a handler");

    hookRegistry[name] = hookRegistry[name] || [];

    var record = listenerRecord(handler, options);

    hookRegistry[name].push(record);

    hookRegistry[name].sort(function (a, b) { return b.priority - a.priority; });

    diagnostics.hooks[name] = hookRegistry[name].length;

    return function () { hookRegistry[name] = hookRegistry[name].filter(function (item) { return item !== record; }); diagnostics.hooks[name] = hookRegistry[name].length; };

  }

 

  function runHook(name, context, options) {

    var records = (hookRegistry[name] || []).slice();

    var configured = configGet("hooks." + name, []);

    if (!Array.isArray(configured)) configured = configured ? [configured] : [];

    configured.forEach(function (handler) { if (isFunction(handler)) records.push(listenerRecord(handler)); });

    var chain = Promise.resolve(context);

    records.forEach(function (record) {

      chain = chain.then(function (current) {

        return toPromise(record.handler(current, ROOT)).then(function (result) {

          if (record.once) hookRegistry[name] = hookRegistry[name].filter(function (item) { return item !== record; });

          return result === undefined ? current : result;

        });

      });

    });

    return withTimeout(chain, options && options.timeoutMs, "Hook " + name);

  }

 

  var hooks = { add: addHook, on: addHook, run: runHook, list: function () { return clone(diagnostics.hooks); }, clear: function (name) { if (name) hookRegistry[name] = []; else hookRegistry = {}; } };

 

  var engines = {};

  function resolveEngine(name) {

    if (engines[name] && engines[name].instance) return engines[name].instance;

    var aliases = {

      rules: ["ruleEngine", "rules"], offers: ["offerEngine", "offers"],

      personalization: ["personalizationEngine", "personalization"],

      recommendations: ["recommendationEngine", "recommendations"],

      experiments: ["experimentEngine", "experiments"], ui: ["ui"], analytics: ["analytics", "analyticsService"]

    };

    var candidates = aliases[name] || [name];

    for (var i = 0; i < candidates.length; i += 1) if (ROOT[candidates[i]]) return ROOT[candidates[i]];

    return null;

  }

 

  function registerEngine(name, instance, metadata) {

    if (!isString(name) || !name) throw new TypeError("registerEngine requires a name");

    if (!instance) throw new TypeError("registerEngine requires an engine instance");

    metadata = metadata || {};

    var existing = engines[name];

    if (existing && existing.instance !== instance) warn("ENGINE_REPLACED", "Replacing registered engine: " + name, { previousVersion: existing.version, nextVersion: instance.version || metadata.version });

    var record = {

      name: name,

      instance: instance,

      version: instance.version || metadata.version || null,

      requiredCore: metadata.requiredCore || instance.requiredCore || null,

      registeredAt: isoNow(),

      initialized: !!(instance.initialized || instance.started),

      healthy: true,

      metadata: clone(metadata)

    };

    if (record.requiredCore && !satisfiesVersion(VERSION, record.requiredCore)) {

      record.healthy = false;

      warn("ENGINE_VERSION_MISMATCH", "Engine " + name + " requires core " + record.requiredCore, { coreVersion: VERSION });

    }

    engines[name] = record;

    diagnostics.engines[name] = engineHealth(name);

    emit("dhcommerce:engine-registered", { name: name, version: record.version, healthy: record.healthy }, { source: "core" });

    return instance;

  }

 

  function unregisterEngine(name) {

    if (!engines[name]) return false;

    var record = engines[name];

    delete engines[name];

    delete diagnostics.engines[name];

    emit("dhcommerce:engine-unregistered", { name: name, version: record.version }, { source: "core" });

    return true;

  }

 

  function countRegistry(instance) {

    if (!instance) return null;

    try {

      if (isFunction(instance.getAll)) { var all = instance.getAll(); return Array.isArray(all) ? all.length : (isObject(all) ? Object.keys(all).length : null); }

      if (isFunction(instance.list)) { var list = instance.list(); return Array.isArray(list) ? list.length : null; }

      if (instance.registry) return Array.isArray(instance.registry) ? instance.registry.length : Object.keys(instance.registry).length;

    } catch (error) { return null; }

    return null;

  }

 

  function engineHealth(name) {

    var record = engines[name];

    var instance = record ? record.instance : resolveEngine(name);

    if (!instance) return { name: name, present: false, initialized: false, healthy: false, version: null, count: null, status: "missing" };

    var reported = null;

    try {

      if (isFunction(instance.getHealth)) reported = instance.getHealth();

      else if (isFunction(instance.health)) reported = instance.health();

    } catch (error) { reported = { healthy: false, error: error.message }; }

    var initialized = !!(instance.initialized || instance.started || getPath(reported, "initialized", false));

    var healthy = reported && hasOwn.call(reported, "healthy") ? reported.healthy !== false : !(record && record.healthy === false);

    return merge({

      name: name,

      present: true,

      initialized: initialized,

      healthy: healthy,

      version: instance.version || (record && record.version) || null,

      count: countRegistry(instance),

      status: healthy ? (initialized ? "ready" : "loaded") : "degraded"

    }, isPlainObject(reported) ? reported : {});

  }

 

  var engineApi = {

    register: registerEngine,

    unregister: unregisterEngine,

    resolve: resolveEngine,

    has: function (name) { return !!resolveEngine(name); },

    get: function (name) { return resolveEngine(name); },

    names: function () { return Object.keys(engines); },

    health: engineHealth,

    getAllHealth: function () {

      var names = ["core", "rules", "offers", "personalization", "recommendations", "experiments", "ui", "config", "bootstrap", "analytics"];

      Object.keys(engines).forEach(function (name) { if (names.indexOf(name) === -1) names.push(name); });

      var result = {};

      names.forEach(function (name) { result[name] = name === "core" ? getHealth() : engineHealth(name); });

      return result;

    }

  };

 

  var plugins = {};

  function use(plugin, options) {

    if (!plugin) throw new TypeError("DHCommerce.use requires a plugin");

    var descriptor = isFunction(plugin) ? { name: plugin.name || uniqueId("plugin"), install: plugin } : plugin;

    var name = descriptor.name || uniqueId("plugin");

    if (plugins[name]) return plugins[name].result;

    if (descriptor.requiredCore && !satisfiesVersion(VERSION, descriptor.requiredCore)) throw new Error("Plugin " + name + " requires core " + descriptor.requiredCore);

    var result = isFunction(descriptor.install) ? descriptor.install(ROOT, options || {}) : null;

    plugins[name] = { name: name, version: descriptor.version || null, installedAt: isoNow(), descriptor: descriptor, result: result };

    diagnostics.plugins[name] = { version: descriptor.version || null, installedAt: plugins[name].installedAt };

    emit("dhcommerce:plugin-installed", { name: name, version: descriptor.version || null }, { source: "core" });

    return result;

  }

 

  function removePlugin(name) {

    var record = plugins[name];

    if (!record) return false;

    if (record.descriptor && isFunction(record.descriptor.uninstall)) safeCall(record.descriptor.uninstall, record.descriptor, [ROOT], null, { code: "PLUGIN_UNINSTALL_FAILED", plugin: name });

    delete plugins[name];

    delete diagnostics.plugins[name];

    emit("dhcommerce:plugin-removed", { name: name }, { source: "core" });

    return true;

  }

 

  function forwardAnalytics(envelope) {

    if (envelope.name.indexOf("dhcommerce:analytics-forwarded") === 0) return;

    if (configGet("analytics.enabled", true) === false) return;

    var analytics = resolveEngine("analytics");

    if (!analytics) return;

    try {

      if (isFunction(analytics.track)) analytics.track(envelope.name, envelope.detail, { source: envelope.source, timestamp: envelope.timestamp });

      else if (isFunction(analytics.emit)) analytics.emit(envelope.name, envelope.detail);

      increment("analytics.forwarded");

    } catch (error) { captureError(error, { code: "ANALYTICS_FORWARD_FAILED", event: envelope.name, silent: true }); }

  }

 

  function getHealth() {

    var fatalErrors = diagnostics.errors.filter(function (entry) { return entry.context && entry.context.fatal; });

    return {

      name: "core",

      present: true,

      initialized: true,

      healthy: fatalErrors.length === 0,

      status: fatalErrors.length ? "degraded" : "ready",

      version: VERSION,

      build: BUILD,

      uptimeMs: now() - STARTED_AT,

      enginesRegistered: Object.keys(engines).length,

      pluginsInstalled: Object.keys(plugins).length,

      errors: diagnostics.errors.length,

      warnings: diagnostics.warnings.length,

      events: diagnostics.events.length

    };

  }

 

  function getDiagnostics(options) {

    options = options || {};

    var output = clone(diagnostics);

    output.health = getHealth();

    output.engineHealth = engineApi.getAllHealth();

    output.state = options.includeState === false ? undefined : state.snapshot();

    output.config = options.includeConfig && ROOT.config && isFunction(ROOT.config.snapshot) ? ROOT.config.snapshot() : undefined;

    output.generatedAt = isoNow();

    return output;

  }

 

  function clearDiagnostics(options) {

    options = options || {};

    diagnostics.events.length = 0;

    diagnostics.logs.length = 0;

    diagnostics.errors.length = 0;

    diagnostics.warnings.length = 0;

    if (options.counters !== false) diagnostics.counters = {};

    emit("dhcommerce:diagnostics-cleared", {}, { source: "core" });

  }

 

  function exposeStateCompatibility() {

    var existing = ROOT.state;

    if (existing && existing !== stateData && isPlainObject(existing)) stateData = merge(stateData, existing);

    ROOT.state = stateData;

    ROOT.stateApi = state;

  }

 

  var core = {

    name: "core",

    version: VERSION,

    build: BUILD,

    initialized: true,

    startedAt: new Date(STARTED_AT).toISOString(),

    logger: logger,

    events: events,

    state: state,

    storage: storage,

    hooks: hooks,

    engines: engineApi,

    diagnostics: { get: getDiagnostics, clear: clearDiagnostics, captureError: captureError, warn: warn, increment: increment },

    utils: {

      noop: noop, identity: identity, isFunction: isFunction, isString: isString, isNumber: isNumber,

      isObject: isObject, isPlainObject: isPlainObject, isPromise: isPromise,

      clone: clone, merge: merge, freezeDeep: freezeDeep, getPath: getPath, setPath: setPath,

      deletePath: deletePath, uniqueId: uniqueId, debounce: debounce, throttle: throttle,

      safeJsonParse: safeJsonParse, safeJsonStringify: safeJsonStringify, hashString: hashString,

      compareVersions: compareVersions, satisfiesVersion: satisfiesVersion, safeCall: safeCall,

      toPromise: toPromise, withTimeout: withTimeout, now: now, isoNow: isoNow

    },

    registerEngine: registerEngine,

    unregisterEngine: unregisterEngine,

    resolveEngine: resolveEngine,

    use: use,

    removePlugin: removePlugin,

    getHealth: getHealth,

    getDiagnostics: getDiagnostics,

    clearDiagnostics: clearDiagnostics,

    captureError: captureError,

    warn: warn

  };

 

  ROOT.version = ROOT.version || VERSION;

  ROOT.build = ROOT.build || BUILD;

  ROOT.core = core;

  ROOT.events = ROOT.events || events;

  ROOT.storage = ROOT.storage || storage;

  ROOT.hooks = ROOT.hooks || hooks;

  ROOT.engines = ROOT.engines || engineApi;

  ROOT.utils = ROOT.utils || core.utils;

  ROOT.logger = ROOT.logger || logger;

  ROOT.use = ROOT.use || use;

  ROOT.getHealth = ROOT.getHealth || function () { return engineApi.getAllHealth(); };

  ROOT.getDiagnostics = ROOT.getDiagnostics || getDiagnostics;

 

  exposeStateCompatibility();

  registerEngine("core", core, { version: VERSION });

 

  ["rules", "offers", "personalization", "recommendations", "experiments", "ui", "config", "bootstrap", "analytics"].forEach(function (name) {

    var instance = resolveEngine(name);

    if (instance && !engines[name]) registerEngine(name, instance, { discovered: true });

  });

 

  if (window.addEventListener) {

    window.addEventListener("error", function (event) {

      if (!event) return;

      captureError(event.error || new Error(event.message || "Window error"), { code: "WINDOW_ERROR", source: event.filename || null, line: event.lineno || null, silent: false });

    });

    window.addEventListener("unhandledrejection", function (event) {

      captureError(event && event.reason ? event.reason : new Error("Unhandled promise rejection"), { code: "UNHANDLED_REJECTION", silent: false });

    });

  }

 

  emit("dhcommerce:core-ready", { version: VERSION, build: BUILD, health: getHealth() }, { source: "core" });

  logger.info("DH Commerce core ready", { version: VERSION, build: BUILD });

})(window, document);